// v0.6.9 — Settings > Exchange Rates. Operator uploads a rate table
// once (Excel) instead of re-entering rates on every calc; the
// Calculate screen's "Load preset rates" button (FxRatesSection.jsx)
// pre-fills from this same table.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CurrencyEur, DownloadSimple, UploadSimple, Trash } from "@phosphor-icons/react";
import { listFxRatePresets, uploadFxRatePreset, deleteFxRatePreset } from "../lib/authApi";
import { downloadFxRatesTemplate, downloadCurrentFxRates } from "../lib/api/downloads";
import { Section } from "./SettingsPrimitives";
import { serverErrorText } from "../lib/serverErrorText";

// v0.6.12 (A1-26) — judge a rate against the operator's OWN other entries
// for the same pair, never against a hardcoded "correct" level. A fixed band
// is what failed before: engine/fx_national carried GBP_PLAUSIBLE 0.5..1.5
// to catch an inverted GBP→EUR rate, and 0.84 (right) and 1.19 (wrong) both
// sit inside it. Rates also move, so a stale guess is worse than no check.
// MonthlyFxRates.jsx reached the same conclusion independently.
const ratesForPair = (r, all) =>
  (all || [])
    .filter((x) => x.source === r.source && x.target === r.target && x.id !== r.id)
    .map((x) => Number(x.rate))
    .filter((n) => Number.isFinite(n) && n > 0);

export const medianForPair = (r, all) => {
  const xs = ratesForPair(r, all).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = Math.floor(xs.length / 2);
  const m = xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
  return m < 1 ? m.toFixed(4) : m.toFixed(2);
};

// An inverted entry is wrong by the SQUARE of the rate, so it is orders of
// magnitude out, not a few percent. 5x keeps ordinary month-to-month drift
// quiet while an inversion cannot hide.
//
// A row is marked when MOST of the operator's other entries for that pair
// disagree with it by more than 5x. Stated that way rather than as a distance
// from the median, because a median is not robust at these sizes and the gate
// caught both failures: judged against its siblings ALONE, the correct 0.1176
// sat 36x from median(0.1172, 8.505) = 4.31 and was condemned; folded into the
// population, the even-sized two-row case put the bad 8.505 only 2x from
// median(0.1176, 8.505) and let it through. Counting who agrees has neither
// problem, and says the honest thing when a pair has exactly two rows that
// disagree: one of them is wrong, so mark both rather than guess.
//
// medianForPair stays sibling-only on purpose — it is the tooltip's "every
// OTHER entry is around ..." figure, not the test.
const DISAGREEMENT_FACTOR = 5;

export const looksInverted = (r, all) => {
  const n = Number(r.rate);
  if (!Number.isFinite(n) || n <= 0) return false;
  const others = ratesForPair(r, all);
  // A lone row for a pair has nothing to disagree with, so it is never marked.
  if (others.length === 0) return false;
  const agreeing = others.filter(
    (o) => n / o <= DISAGREEMENT_FACTOR && o / n <= DISAGREEMENT_FACTOR,
  ).length;
  return agreeing * 2 < others.length;
};

export const ExchangeRatesTab = ({ isAdmin }) => {
  const { t } = useTranslation();
  const [rates, setRates] = useState([]);
  // A2-506 — saved rates that disagree with each other (server-computed).
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listFxRatePresets();
      setRates(data?.rates || []);
      setConflicts(data?.conflicts || []);
    } catch {
      /* leave list as-is; the panel still renders */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) reload();
  }, [isAdmin, reload]);

  if (!isAdmin) return null;

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setUploadResult(null);
    try {
      const result = await uploadFxRatePreset(file);
      setUploadResult(result);
      await reload();
    } catch (err) {
      // A2-506 — the refusal used to land here as an object, under "0
      // failed", which the panel never shows: the operator saw "0 row(s)
      // saved" and nothing else, six times in a row.
      setUploadResult({
        uploaded: 0, failed: 1,
        failures: [serverErrorText(err?.response?.data?.detail, err?.message || "Upload failed")],
      });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    setBusy(true);
    try {
      await deleteFxRatePreset(id);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const conflictIds = new Set(conflicts.flatMap((c) => c.rate_ids || []));
  const months = new Set(rates.map((r) => r.month));
  const pairs = new Set(rates.map((r) => `${r.source}->${r.target}`));

  return (
    <Section titleKey="ui.settings.sections.exchange_rates" icon={<CurrencyEur size={14} weight="bold" />}>
      <div className="text-xs text-slate-500 dark:text-slate-400">
        {t("ui.settings.fx_presets.intro", { defaultValue: "Upload a rate table once and it pre-fills the exchange-rate boxes on the Calculate screen — you can still hand-edit a value for one specific calc, which takes precedence for that calc only." })}
      </div>

      <div className="flex items-center gap-4 text-xs">
        <span data-testid="fx-rates-summary" className="text-slate-600 dark:text-slate-300">
          {loading ? t("ui.settings.fx_presets.loading", { defaultValue: "Loading…" }) : (
            rates.length === 0
              ? t("ui.settings.fx_presets.empty", { defaultValue: "No exchange rates saved yet." })
              : t("ui.settings.fx_presets.summary", { count: rates.length, pairs: pairs.size, months: months.size, defaultValue: `${rates.length} entr${rates.length === 1 ? "y" : "ies"} · ${pairs.size} currency pair(s) · ${months.size} month(s)` })
          )}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="fx-rates-download-template"
          onClick={downloadFxRatesTemplate}
          className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 dark:text-teal-300 bg-teal-600/10 hover:bg-teal-600/20 border border-teal-400/50 px-3 py-1.5 rounded-xl"
        >
          <DownloadSimple size={13} weight="bold" /> {t("ui.settings.fx_presets.download_template", { defaultValue: "Download template" })}
        </button>
        <button
          type="button"
          data-testid="fx-rates-download-current"
          onClick={downloadCurrentFxRates}
          disabled={rates.length === 0}
          className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-600/10 hover:bg-slate-600/20 border border-slate-400/50 px-3 py-1.5 rounded-xl disabled:opacity-50"
        >
          <DownloadSimple size={13} weight="bold" /> {t("ui.settings.fx_presets.download_current", { defaultValue: "Download current rates" })}
        </button>
        <label
          data-testid="fx-rates-upload-label"
          className={`inline-flex items-center gap-1 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 border border-teal-700 px-3 py-1.5 rounded-xl cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}
        >
          <UploadSimple size={13} weight="bold" /> {t("ui.settings.fx_presets.upload_filled", { defaultValue: "Upload filled template" })}
          <input
            type="file"
            data-testid="fx-rates-upload-input"
            accept=".xlsx"
            onChange={onFileChosen}
            className="hidden"
            disabled={busy}
          />
        </label>
      </div>

      {uploadResult && (
        <div data-testid="fx-rates-upload-result" aria-live="polite" className="text-xs border border-slate-200 dark:border-slate-700 rounded-xl p-3 space-y-1">
          <div className={uploadResult.uploaded > 0 ? "text-emerald-700 dark:text-emerald-300" : "font-bold text-red-700 dark:text-red-300"}>
            {uploadResult.uploaded > 0
              ? t("ui.settings.fx_presets.rows_saved", { n: uploadResult.uploaded, defaultValue: `${uploadResult.uploaded} rate(s) saved.` })
              : t("ui.settings.fx_presets.nothing_saved", { defaultValue: "Nothing was saved. The reasons are below." })}
          </div>
          {(uploadResult.notes || []).map((n, i) => (
            <div key={`note-${i}`} data-testid="fx-rates-upload-note" className="text-slate-600 dark:text-slate-300">{n}</div>
          ))}
          {(uploadResult.failures || []).length > 0 && (
            <div className="text-amber-700 dark:text-amber-300">
              {t("ui.settings.fx_presets.rows_skipped", { n: uploadResult.failed, defaultValue: `${uploadResult.failed} row(s) not saved:` })}
              <ul data-testid="fx-rates-upload-failures" className="list-disc list-inside mt-1">
                {uploadResult.failures.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {conflicts.length > 0 && (
        <div data-testid="fx-rates-saved-conflicts" role="alert" className="text-xs border border-amber-400/60 bg-amber-50 dark:bg-amber-950/30 rounded-xl p-3 space-y-1 text-amber-800 dark:text-amber-200">
          <div className="font-bold">
            {t("ui.settings.fx_presets.saved_conflicts", { defaultValue: "Some saved rates disagree with each other, so at least one of them is wrong. Every upload that includes them is refused until it is fixed. Find the marked rows below, delete the wrong one, and upload it again." })}
          </div>
          <ul className="list-disc list-inside">
            {conflicts.map((c, i) => <li key={i}>{c.message}</li>)}
          </ul>
        </div>
      )}

      {rates.length > 0 && (
        <div className="border border-slate-200 dark:border-slate-700 max-h-64 overflow-y-auto">
          {/* v0.6.12 (A1-26) — THIS TABLE USED TO READ "GBP → DKK  8.505",
              and the arrow is what made an upside-down rate look right: an
              arrow reads as "convert GBP into DKK", so 8.505 (DKK per GBP)
              seems correct, when the stored figure is GBP per DKK and
              should be 0.1176. A Danish return came out 72x too small.
              There was no header row either, so nothing said what the
              number meant.

              So the rate is now rendered as a SENTENCE — "8.505 GBP = 1
              DKK" — which is self-evidently absurd and needs no tax
              knowledge to spot. Same principle as MonthlyFxRates'
              live £100 → €X preview: show what the number MEANS and a
              wrong one gives itself away. */}
          <div className="grid grid-cols-12 gap-2 px-3 py-1.5 border-b-2 border-slate-300 dark:border-slate-600 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 sticky top-0 bg-white dark:bg-slate-900">
            <span className="col-span-5">{t("ui.settings.fx_presets.col_rate", { defaultValue: "Rate, read out in full" })}</span>
            <span className="col-span-2">{t("ui.settings.fx_presets.col_month", { defaultValue: "Month" })}</span>
            <span className="col-span-2">{t("ui.settings.fx_presets.col_applies_to", { defaultValue: "Applies to" })}</span>
            <span className="col-span-2">{t("ui.settings.fx_presets.col_source", { defaultValue: "Source" })}</span>
            <span className="col-span-1" />
          </div>
          {rates.map((r) => (
            <div
              key={r.id}
              data-testid={`fx-rate-row-${r.id}`}
              data-conflict={conflictIds.has(r.id) ? "true" : undefined}
              className={`grid grid-cols-12 gap-2 px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-xs items-center ${conflictIds.has(r.id) ? "bg-amber-100 dark:bg-amber-900/40" : ""}`}
            >
              <span className="col-span-5 text-slate-900 dark:text-slate-50">
                <span className="tabular-nums font-bold text-teal-700 dark:text-teal-300">{r.rate}</span>
                {" "}
                <span className="font-bold">{r.source}</span>
                {" = 1 "}
                <span className="font-bold">{r.target}</span>
                {looksInverted(r, rates) && (
                  <span
                    data-testid={`fx-rate-suspect-${r.id}`}
                    title={t("ui.settings.fx_presets.suspect_title", { src: r.source, tgt: r.target, median: medianForPair(r, rates), defaultValue: `Every other ${r.source}→${r.target} entry is around ${medianForPair(r, rates)}. A value this far off is usually the rate upside down — the Rate column wants ${r.source} per 1 ${r.target}.` })}
                    className="ml-2 text-amber-700 dark:text-amber-300 font-bold cursor-help"
                  >
                    {t("ui.settings.fx_presets.check_direction", { defaultValue: "⚠ check direction" })}
                  </span>
                )}
              </span>
              <span className="col-span-2 tabular-nums text-slate-600 dark:text-slate-300">{r.month}</span>
              {/* v0.6.10 — a pair/month can now carry TWO rows (one
                  Period-End, one Monthly — see routes_settings_fx_
                  rates.py's docstring); without this, they'd render
                  identically and an operator couldn't tell which is
                  which, or safely delete the right one. */}
              <span
                data-testid={`fx-rate-type-${r.id}`}
                className={`col-span-2 font-bold ${
                  r.rate_type === "period"
                    ? "text-indigo-700 dark:text-indigo-300"
                    : r.rate_type === "monthly"
                      ? "text-amber-700 dark:text-amber-300"
                      : "text-slate-500 dark:text-slate-400"
                }`}
              >
                {r.rate_type === "period" ? "Period-End"
                  : r.rate_type === "monthly" ? "Monthly" : "Both"}
              </span>
              <span className="col-span-3 text-slate-500 dark:text-slate-400 truncate">{r.note}</span>
              <span className="col-span-1 text-right">
                <button
                  type="button"
                  data-testid={`fx-rate-delete-${r.id}`}
                  onClick={() => onDelete(r.id)}
                  disabled={busy}
                  title={t("ui.settings.remove_entry", { defaultValue: "Remove this entry" })}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50"
                >
                  <Trash size={13} weight="bold" />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};
